import { BusinessAuto, InlandMarineEquipment, Tractor, Trailer } from "@deathstar/types/northstar";
import { BusinessAutoDto, DriverDto, DriverRow, EquipmentRow, ToolDto, TractorDto, TrailerDto } from "@deathstar/types/waypoint";
import { PrimaryButton, useSnackbar } from "@deathstar/ui";
import {
    ArrowUpTrayIcon,
    ArrowUturnLeftIcon,
    CheckCircleIcon,
    ClockIcon,
    ExclamationCircleIcon,
    PaperClipIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";
import { CircularProgress, Tooltip } from "@material-ui/core";
import { createContext, lazy, Suspense, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "../../api/api";
import { EquipmentTypeString } from "../../api/queries/equipment";
import { useAccountId } from "../../api/useAccountId";
import { blazar } from "../../api/util/blazar";
import { Dialog } from "../../components/dialog/Dialog";
import SimpleConfirmation from "../../components/simple-confirmation/SimpleConfirmation";
import { queryClient } from "../../util/queryClient";

const NewDriverDialog = lazy(() => import("../drivers/NewDriverDialog").then((module) => ({ default: module.NewDriverDialog })));
const NewUnitDialog = lazy(() => import("../equipment/NewUnitDialog").then((module) => ({ default: module.NewUnitDialog })));

export type PendingPolicyChangeKind = "drivers" | "equipment";

export type PendingPolicyChange = {
    id: string;
    accountId: string;
    kind: PendingPolicyChangeKind;
    label: string;
    detail?: string;
    action?: "update" | "remove";
    submit: () => Promise<void>;
    /** Documents the client attached (lease termination, rental agreement, etc.), sent to the account manager once the change succeeds */
    supportingDocuments?: File[];
    unitUpdate?:
        | {
              kind: "drivers";
              id?: number;
              isCreate?: boolean;
              changes: Partial<DriverDto>;
              displayValues?: Partial<DriverDto>;
              originalValues?: Partial<DriverDto>;
          }
        | {
              kind: "equipment";
              type: EquipmentTypeString;
              id?: number;
              isCreate?: boolean;
              changes: Partial<TractorDto | TrailerDto | BusinessAutoDto | ToolDto>;
              displayValues?: Partial<TractorDto | TrailerDto | BusinessAutoDto | ToolDto>;
              originalValues?: Partial<TractorDto | TrailerDto | BusinessAutoDto | ToolDto>;
          };
};

type PendingPolicyAdditionRow = {
    pendingPolicyAdditionId: string;
};

export function isPendingPolicyAdditionRow(row: unknown): row is PendingPolicyAdditionRow {
    return !!row && typeof row === "object" && "pendingPolicyAdditionId" in row;
}

export function getPendingPolicyAdditionChange(changes: PendingPolicyChange[], row: unknown): PendingPolicyChange | undefined {
    if (!isPendingPolicyAdditionRow(row)) {
        return undefined;
    }
    return changes.find((change) => change.id === row.pendingPolicyAdditionId);
}

function getPendingPolicyAdditionRowId(changeId: string) {
    const value = Number.parseInt(changeId.replaceAll("-", "").slice(0, 8), 16);
    return -(Number.isNaN(value) ? 1 : value + 1);
}

function getDisplayCoverages(coverages: DriverDto["coverages"] | undefined) {
    return coverages?.filter((coverage) => !coverage.remove) as DriverRow["coverages"];
}

export function withPendingDriverAdditions(rows: DriverRow[], changes: PendingPolicyChange[], accountId: string): DriverRow[] {
    const listedIds = new Set(rows.map((row) => row.id));
    const additions = changes
        .filter(
            (change): change is DriverUnitUpdate =>
                change.accountId === accountId &&
                change.label.startsWith("Add ") &&
                change.unitUpdate?.kind === "drivers" &&
                (change.unitUpdate.isCreate || change.unitUpdate.id === undefined || !listedIds.has(change.unitUpdate.id))
        )
        .map((change) => {
            const values = { ...change.unitUpdate.displayValues, ...change.unitUpdate.changes } as Partial<DriverDto>;
            return {
                ...values,
                id: change.unitUpdate.id ?? getPendingPolicyAdditionRowId(change.id),
                accountId,
                coverages: getDisplayCoverages(values.coverages),
                policyDoh: values.doh,
                policyExp: values.exp,
                policyOwnerOperator: values.ownerOperator,
                employerId: typeof values.employer === "number" ? values.employer : undefined,
                employer: typeof values.employer === "object" ? values.employer : undefined,
                pendingPolicyAdditionId: change.id,
            } as DriverRow & PendingPolicyAdditionRow;
        });

    return [...additions, ...rows];
}

export function withPendingEquipmentAdditions<T extends Tractor | Trailer | BusinessAuto | InlandMarineEquipment>(
    rows: EquipmentRow<T>[],
    changes: PendingPolicyChange[],
    accountId: string,
    type: EquipmentTypeString
): EquipmentRow<T>[] {
    const listedIds = new Set(rows.map((row) => row.id));
    const additions = changes
        .filter(
            (change): change is EquipmentUnitUpdate =>
                change.accountId === accountId &&
                change.label.startsWith("Add ") &&
                change.unitUpdate?.kind === "equipment" &&
                change.unitUpdate.type === type &&
                (change.unitUpdate.isCreate || change.unitUpdate.id === undefined || !listedIds.has(change.unitUpdate.id))
        )
        .map((change) => {
            const values = { ...change.unitUpdate.displayValues, ...change.unitUpdate.changes } as Partial<
                TractorDto & TrailerDto & BusinessAutoDto & ToolDto
            >;
            return {
                ...values,
                id: change.unitUpdate.id ?? getPendingPolicyAdditionRowId(change.id),
                accountId,
                coverages: getDisplayCoverages(values.coverages),
                policyValue: values.value,
                policyAcv: values.acv,
                policyOwnerOperator: "ownerOperator" in values ? values.ownerOperator : undefined,
                pendingPolicyAdditionId: change.id,
            } as unknown as EquipmentRow<T> & PendingPolicyAdditionRow;
        });

    return [...additions, ...rows];
}

export function getDriverChangeDetail(name: string | null | undefined) {
    const parts = (name || "").trim().split(/\s+/).filter(Boolean);
    return parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1][0]}.` : parts[0];
}

export function getEquipmentChangeDetail(vin: string | null | undefined) {
    const last4 = vin?.slice(-4).toUpperCase();
    return last4 ? `#${last4}` : undefined;
}

type SubmitResult = {
    change: PendingPolicyChange;
    status: "pending" | "success" | "failed";
    error?: string;
};

type SavedSubmitResult = Pick<SubmitResult, "status" | "error">;

type UnitUpdateChange = PendingPolicyChange & { unitUpdate: NonNullable<PendingPolicyChange["unitUpdate"]> };
type DriverUnitUpdate = UnitUpdateChange & {
    unitUpdate: Extract<NonNullable<PendingPolicyChange["unitUpdate"]>, { kind: "drivers" }>;
};
type EquipmentUnitUpdate = UnitUpdateChange & {
    unitUpdate: Extract<NonNullable<PendingPolicyChange["unitUpdate"]>, { kind: "equipment" }>;
};

export function getPendingChangeIntent(change: PendingPolicyChange): "update" | "remove" {
    if (change.action) {
        return change.action;
    }
    return change.label.startsWith("Remove ") ? "remove" : "update";
}

export function getPendingDriverChanges(changes: PendingPolicyChange[], accountId: string, id: number): DriverUnitUpdate[] {
    return changes.filter(
        (change): change is DriverUnitUpdate =>
            change.accountId === accountId &&
            change.unitUpdate?.kind === "drivers" &&
            !change.unitUpdate.isCreate &&
            change.unitUpdate.id === id
    );
}

export function getPendingEquipmentChanges(
    changes: PendingPolicyChange[],
    accountId: string,
    type: EquipmentTypeString,
    id: number
): EquipmentUnitUpdate[] {
    return changes.filter(
        (change): change is EquipmentUnitUpdate =>
            change.accountId === accountId &&
            change.unitUpdate?.kind === "equipment" &&
            !change.unitUpdate.isCreate &&
            change.unitUpdate.type === type &&
            change.unitUpdate.id === id
    );
}

export function getPendingDriverPolicyChangePriority(changes: PendingPolicyChange[], row: DriverRow) {
    return Number(
        !!row.pendingChanges?.length ||
            !!getPendingPolicyAdditionChange(changes, row) ||
            getPendingDriverChanges(changes, row.accountId, row.id).length > 0
    );
}

export function getPendingEquipmentPolicyChangePriority(
    changes: PendingPolicyChange[],
    row: EquipmentRow<Tractor | Trailer | BusinessAuto | InlandMarineEquipment>,
    type: EquipmentTypeString
) {
    return Number(
        !!row.pendingChanges?.length ||
            !!getPendingPolicyAdditionChange(changes, row) ||
            getPendingEquipmentChanges(changes, row.accountId, type, row.id).length > 0
    );
}

export function getPendingDriverChangeValues(changes: PendingPolicyChange[], accountId: string, id: number): Partial<DriverDto> {
    return Object.assign({}, ...getPendingDriverChanges(changes, accountId, id).map((change) => change.unitUpdate.changes));
}

export function getPendingEquipmentChangeValues(
    changes: PendingPolicyChange[],
    accountId: string,
    type: EquipmentTypeString,
    id: number
): Partial<TractorDto | TrailerDto | BusinessAutoDto | ToolDto> {
    return Object.assign({}, ...getPendingEquipmentChanges(changes, accountId, type, id).map((change) => change.unitUpdate.changes));
}

export function getPendingPolicyChangeTitle(change: PendingPolicyChange) {
    return `${change.label}${change.detail ? ` ${change.detail}` : ""}`;
}

/** True when the unit has a not-yet-completed change that removes it (the row is highlighted red) */
export function hasPendingRemoval(changes: PendingPolicyChange[]) {
    return changes.some((change) => getPendingChangeIntent(change) === "remove");
}

export function getPendingPolicyChangeRowClass(changes: PendingPolicyChange[]) {
    if (hasPendingRemoval(changes)) {
        return "bg-red-50/80";
    }
    return changes.length ? "bg-yellow-50/70" : "";
}

export function PendingPolicyChangeIndicator({
    changes,
    appearance = "default",
}: {
    changes: PendingPolicyChange[];
    appearance?: "default" | "addition";
}) {
    if (!changes.length) {
        return null;
    }

    const color =
        appearance === "addition"
            ? "text-emerald-700"
            : changes.some((change) => getPendingChangeIntent(change) === "remove")
            ? "bg-red-100 text-red-700"
            : "bg-yellow-100 text-amber-700";

    return (
        <Tooltip
            classes={{ tooltip: "bg-white shadow-lg rounded p-2 text-stone-800 font-normal border" }}
            title={
                <div>
                    {changes.map((change) => (
                        <p key={change.id}>{getPendingPolicyChangeTitle(change)}</p>
                    ))}
                </div>
            }
        >
            <span className={`inline-flex h-6 w-6 cursor-help items-center justify-center rounded-full ${color}`}>
                <ClockIcon className="h-4 w-4" />
            </span>
        </Tooltip>
    );
}

type UnitUpdateValidationResult = {
    clientId: string;
    error?: string;
};

function hasUnitUpdate(change: PendingPolicyChange): change is UnitUpdateChange {
    return !!change.unitUpdate;
}

function getDriverUpdates(changes: PendingPolicyChange[]): DriverUnitUpdate[] {
    return changes.filter((change): change is DriverUnitUpdate => change.unitUpdate?.kind === "drivers");
}

function getEquipmentUpdates(changes: PendingPolicyChange[]): EquipmentUnitUpdate[] {
    return changes.filter((change): change is EquipmentUnitUpdate => change.unitUpdate?.kind === "equipment");
}

function getDriverPayload(changes: DriverUnitUpdate[]) {
    return changes.map(({ id: clientId, unitUpdate }) => ({ clientId, id: unitUpdate.id, ...unitUpdate.changes }));
}

function getEquipmentPayload(changes: EquipmentUnitUpdate[]) {
    return changes.map(({ id: clientId, unitUpdate }) => ({
        clientId,
        type: unitUpdate.type,
        id: unitUpdate.id,
        ...unitUpdate.changes,
    }));
}

function stripClientIds<T extends { clientId?: string }>(updates: T[]) {
    return updates.map(({ clientId: _clientId, ...update }) => update);
}

function isSameUnitUpdate(a: NonNullable<PendingPolicyChange["unitUpdate"]>, b: NonNullable<PendingPolicyChange["unitUpdate"]>) {
    if (a.isCreate || b.isCreate || a.kind !== b.kind || a.id !== b.id) {
        return false;
    }
    return a.kind === "drivers" || b.kind === "drivers" || a.type === b.type;
}

function isRemoveUnitChange(change: NonNullable<PendingPolicyChange["unitUpdate"]>) {
    const coverages = change.changes.coverages;
    return !!coverages?.length && coverages.every((coverage) => coverage.remove);
}

function isFixableChange(change: PendingPolicyChange) {
    return !!change.unitUpdate && !change.unitUpdate.isCreate && !change.label.startsWith("Remove ");
}

function normalizeComparableValue(value: unknown) {
    if (!Array.isArray(value)) {
        return value ?? null;
    }
    if (value.every((item) => item && typeof item === "object" && "policyId" in item && "coverageOptionId" in item)) {
        return value
            .filter((coverage) => !coverage.remove)
            .map((coverage) => `${coverage.policyId}:${coverage.coverageOptionId}`)
            .sort();
    }
    if (value.every((item) => item && typeof item === "object" && "id" in item)) {
        return value.map((item) => item.id).sort((a, b) => a - b);
    }
    return value;
}

function valuesMatch(a: unknown, b: unknown) {
    return JSON.stringify(normalizeComparableValue(a)) === JSON.stringify(normalizeComparableValue(b));
}

function getMeaningfulChangeKeys(changes: Record<string, unknown>) {
    return Object.keys(changes).filter((key) => key !== "atDate");
}

function dropNoopFields<T extends Record<string, unknown>>(changes: T, originalValues?: Record<string, unknown>) {
    if (!originalValues) {
        return changes;
    }

    const next = { ...changes };
    for (const key of getMeaningfulChangeKeys(changes)) {
        if (valuesMatch(changes[key], originalValues[key])) {
            delete next[key];
        }
    }

    return getMeaningfulChangeKeys(next).length ? next : {};
}

function isNoopUnitUpdate(change: NonNullable<PendingPolicyChange["unitUpdate"]>) {
    const originalValues = change.originalValues as Record<string, unknown> | undefined;
    const changes = dropNoopFields(change.changes as Record<string, unknown>, originalValues);
    const meaningfulKeys = getMeaningfulChangeKeys(changes);

    return !meaningfulKeys.length;
}

function normalizePendingChanges(existing: PendingPolicyChange[], incoming: PendingPolicyChange) {
    if (!incoming.unitUpdate) {
        return [...existing, incoming];
    }
    const incomingUpdate = incoming.unitUpdate;

    const sameUnitChanges = existing.filter((change) => change.unitUpdate && isSameUnitUpdate(change.unitUpdate, incomingUpdate));
    if (!sameUnitChanges.length) {
        const normalizedIncoming = {
            ...incoming,
            unitUpdate: {
                ...incomingUpdate,
                changes: dropNoopFields(
                    incomingUpdate.changes as Record<string, unknown>,
                    incomingUpdate.originalValues as Record<string, unknown> | undefined
                ),
            },
        };
        return isNoopUnitUpdate(normalizedIncoming.unitUpdate) ? existing : [...existing, normalizedIncoming];
    }

    const previousUpdate = sameUnitChanges[sameUnitChanges.length - 1].unitUpdate!;
    const originalValues = previousUpdate.originalValues || incomingUpdate.originalValues;
    const changes = dropNoopFields(
        isRemoveUnitChange(incomingUpdate) ? incomingUpdate.changes : { ...previousUpdate.changes, ...incomingUpdate.changes },
        originalValues as Record<string, unknown> | undefined
    );
    const supportingDocuments = [
        ...sameUnitChanges.flatMap((change) => change.supportingDocuments ?? []),
        ...(incoming.supportingDocuments ?? []),
    ];
    const merged = {
        ...incoming,
        supportingDocuments: supportingDocuments.length ? supportingDocuments : undefined,
        unitUpdate: {
            ...incomingUpdate,
            originalValues,
            changes,
        },
    };
    const withoutSameUnit = existing.filter((change) => !change.unitUpdate || !isSameUnitUpdate(change.unitUpdate, incomingUpdate));

    return isNoopUnitUpdate(merged.unitUpdate) ? withoutSameUnit : [...withoutSameUnit, merged];
}

type PendingPolicyChangesContextValue = {
    changes: PendingPolicyChange[];
    addChange: (change: Omit<PendingPolicyChange, "id">) => void;
    removeChange: (id: string) => void;
    clearAccountChanges: (accountId: string) => void;
    reset: (accountId: string) => void;
    getResults: (accountId: string) => SubmitResult[];
    submitAll: (accountId: string) => Promise<SubmitResult[]>;
};

const PendingPolicyChangesContext = createContext<PendingPolicyChangesContextValue | null>(null);

export function PendingPolicyChangesProvider({ children }: { children: React.ReactNode }) {
    const [changes, setChanges] = useState<PendingPolicyChange[]>([]);
    const [savedResults, setSavedResults] = useState<Record<string, SavedSubmitResult>>({});

    useEffect(() => {
        if (!changes.length) {
            return;
        }
        const warn = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = "You have pending policy changes.";
        };
        window.addEventListener("beforeunload", warn);
        return () => window.removeEventListener("beforeunload", warn);
    }, [changes.length]);

    const addChange = useCallback(
        (change: Omit<PendingPolicyChange, "id">) => {
            const incoming = { ...change, id: crypto.randomUUID() };
            const nextChanges = normalizePendingChanges(changes, incoming);
            const message = !nextChanges.some((change) => change.id === incoming.id)
                ? nextChanges.length < changes.length
                    ? "Pending policy change removed"
                    : "No policy changes to request"
                : nextChanges.length <= changes.length
                ? "Pending policy change updated"
                : "Policy change request added";
            setChanges(nextChanges);
            if (incoming.unitUpdate) {
                setSavedResults((existing) =>
                    Object.fromEntries(
                        Object.entries(existing).filter(([id]) => {
                            const savedChange = changes.find((change) => change.id === id);
                            return !savedChange?.unitUpdate || !isSameUnitUpdate(savedChange.unitUpdate, incoming.unitUpdate!);
                        })
                    )
                );
            }
            useSnackbar.add(message, { variant: "info" });
        },
        [changes]
    );

    const removeChange = useCallback((id: string) => {
        setChanges((existing) => existing.filter((change) => change.id !== id));
        setSavedResults((existing) => {
            const next = { ...existing };
            delete next[id];
            return next;
        });
    }, []);

    const clearAccountChanges = useCallback(
        (accountId: string) => {
            const removedIds = new Set(changes.filter((change) => change.accountId === accountId).map((change) => change.id));
            setChanges((existing) => existing.filter((change) => change.accountId !== accountId));
            setSavedResults((existing) => Object.fromEntries(Object.entries(existing).filter(([id]) => !removedIds.has(id))));
        },
        [changes]
    );

    const reset = useCallback(
        (accountId: string) => {
            clearAccountChanges(accountId);
            useSnackbar.add("Pending policy changes discarded", { variant: "info" });
        },
        [clearAccountChanges]
    );

    const getResults = useCallback(
        (accountId: string): SubmitResult[] =>
            changes
                .filter((change) => change.accountId === accountId)
                .map((change) => ({ change, status: savedResults[change.id]?.status || "pending", error: savedResults[change.id]?.error })),
        [changes, savedResults]
    );

    const submitAll = useCallback(
        async (accountId: string) => {
            const accountChanges = changes.filter((change) => change.accountId === accountId);
            const unitUpdates = accountChanges.filter(hasUnitUpdate);
            const directSubmits = accountChanges.filter((change) => !hasUnitUpdate(change));
            const results: SubmitResult[] = [];
            const saveResults = () => {
                setSavedResults((existing) => {
                    const next = { ...existing };
                    for (const result of results) {
                        if (result.status === "failed") {
                            next[result.change.id] = { status: "failed", error: result.error };
                        } else if (result.status === "success" || result.change.unitUpdate) {
                            delete next[result.change.id];
                        }
                    }
                    return next;
                });
            };
            const addPendingResults = () => {
                const resultChangeIds = new Set(results.map((result) => result.change.id));
                results.push(
                    ...accountChanges
                        .filter((change) => !resultChangeIds.has(change.id))
                        .map((change) => ({ change, status: "pending" as const }))
                );
            };

            const submitOne = async (change: PendingPolicyChange) => {
                try {
                    await change.submit();
                    results.push({ change, status: "success" });
                    setChanges((existing) => existing.filter((existingChange) => existingChange.id !== change.id));
                } catch (error) {
                    console.error(error);
                    results.push({ change, status: "failed", error: getErrorMessage(error) });
                }
            };

            const uploadSupportingDocuments = async () => {
                const succeeded = results.filter((result) => result.status === "success" && result.change.supportingDocuments?.length);
                for (const { change } of succeeded) {
                    try {
                        await api.equipment.uploadSupportingDocuments(
                            accountId,
                            change.supportingDocuments!,
                            getPendingPolicyChangeTitle(change)
                        );
                    } catch (error) {
                        console.error(error);
                        useSnackbar.add(
                            `${getPendingPolicyChangeTitle(change)} was submitted, but its supporting documents could not be uploaded.`,
                            { variant: "warning", persist: true }
                        );
                    }
                }
            };

            if (unitUpdates.length) {
                const validateGroup = async (group: PendingPolicyChange[], validate: () => Promise<UnitUpdateValidationResult[]>) => {
                    const validationResults = await validate();
                    const errorById = new Map(
                        validationResults.filter((result) => result.error).map((result) => [result.clientId, result.error])
                    );
                    results.push(
                        ...group
                            .filter((change) => errorById.has(change.id))
                            .map((change) => ({ change, status: "failed" as const, error: errorById.get(change.id) }))
                    );
                    return group.filter((change) => !errorById.has(change.id));
                };
                const submitGroup = async (group: PendingPolicyChange[], submit: () => Promise<void>) => {
                    if (!group.length) {
                        return;
                    }
                    try {
                        await submit();
                        results.push(...group.map((change) => ({ change, status: "success" as const })));
                        setChanges((existing) => existing.filter((change) => !group.some((submitted) => submitted.id === change.id)));
                    } catch (error) {
                        console.error(error);
                        results.push(...group.map((change) => ({ change, status: "failed" as const, error: getErrorMessage(error) })));
                    }
                };
                const driverUpdates = getDriverUpdates(unitUpdates);
                const equipmentUpdates = getEquipmentUpdates(unitUpdates);
                const driverValidationPayload = getDriverPayload(driverUpdates);
                const equipmentValidationPayload = getEquipmentPayload(equipmentUpdates);
                const validUnitUpdates =
                    driverUpdates.length && equipmentUpdates.length
                        ? await validateGroup(unitUpdates, () =>
                              blazar.fetchJson<UnitUpdateValidationResult[]>(`/waypoint/orgs/${accountId}/policy-unit-updates/validate`, {
                                  method: "POST",
                                  body: JSON.stringify({
                                      drivers: driverValidationPayload,
                                      equipment: equipmentValidationPayload,
                                  }),
                              })
                          )
                        : [
                              ...(driverUpdates.length
                                  ? await validateGroup(
                                        unitUpdates.filter((change) => change.unitUpdate?.kind === "drivers"),
                                        () =>
                                            blazar.fetchJson<UnitUpdateValidationResult[]>(
                                                `/waypoint/orgs/${accountId}/driver-policy-updates/validate`,
                                                {
                                                    method: "POST",
                                                    body: JSON.stringify({
                                                        drivers: driverValidationPayload,
                                                    }),
                                                }
                                            )
                                    )
                                  : []),
                              ...(equipmentUpdates.length
                                  ? await validateGroup(
                                        unitUpdates.filter((change) => change.unitUpdate?.kind === "equipment"),
                                        () =>
                                            blazar.fetchJson<UnitUpdateValidationResult[]>(
                                                `/waypoint/orgs/${accountId}/equipment-policy-updates/validate`,
                                                {
                                                    method: "POST",
                                                    body: JSON.stringify({
                                                        equipment: equipmentValidationPayload,
                                                    }),
                                                }
                                            )
                                    )
                                  : []),
                          ];

                if (results.some((result) => result.status === "failed")) {
                    addPendingResults();
                    saveResults();
                    return results;
                }

                const validDriverUpdates = getDriverUpdates(validUnitUpdates);
                const validEquipmentUpdates = getEquipmentUpdates(validUnitUpdates);
                const driverPayload = stripClientIds(getDriverPayload(validDriverUpdates));
                const equipmentPayload = stripClientIds(getEquipmentPayload(validEquipmentUpdates));

                if (validDriverUpdates.length && validEquipmentUpdates.length) {
                    await submitGroup(validUnitUpdates, () =>
                        blazar.fetchJson<void>(`/waypoint/orgs/${accountId}/policy-unit-updates`, {
                            method: "POST",
                            body: JSON.stringify({
                                drivers: driverPayload,
                                equipment: equipmentPayload,
                            }),
                        })
                    );
                } else {
                    await submitGroup(
                        validUnitUpdates.filter((change) => change.unitUpdate?.kind === "drivers"),
                        () =>
                            validDriverUpdates.length
                                ? blazar.fetchJson<void>(`/waypoint/orgs/${accountId}/driver-policy-updates`, {
                                      method: "POST",
                                      body: JSON.stringify({
                                          drivers: driverPayload,
                                      }),
                                  })
                                : Promise.resolve()
                    );
                    await submitGroup(
                        validUnitUpdates.filter((change) => change.unitUpdate?.kind === "equipment"),
                        () =>
                            validEquipmentUpdates.length
                                ? blazar.fetchJson<void>(`/waypoint/orgs/${accountId}/equipment-policy-updates`, {
                                      method: "POST",
                                      body: JSON.stringify({
                                          equipment: equipmentPayload,
                                      }),
                                  })
                                : Promise.resolve()
                    );
                }

                if (results.some((result) => result.status === "failed")) {
                    await uploadSupportingDocuments();
                    addPendingResults();
                    saveResults();
                    return results;
                }
            }

            for (const change of directSubmits) {
                await submitOne(change);
            }
            await uploadSupportingDocuments();
            queryClient.invalidateQueries({ queryKey: api.drivers.queryKeys.find(accountId) });
            queryClient.invalidateQueries({ queryKey: api.equipment.queryKeys.find("tractors", accountId) });
            queryClient.invalidateQueries({ queryKey: api.equipment.queryKeys.find("trailers", accountId) });
            queryClient.invalidateQueries({ queryKey: api.equipment.queryKeys.find("autos", accountId) });
            queryClient.invalidateQueries({ queryKey: api.equipment.queryKeys.find("tools", accountId) });
            queryClient.invalidateQueries({ queryKey: api.monthlyReports.queryKeys.find(accountId) });
            saveResults();
            return results;
        },
        [changes]
    );

    const value = useMemo(
        () => ({ changes, addChange, removeChange, clearAccountChanges, reset, getResults, submitAll }),
        [addChange, changes, clearAccountChanges, getResults, removeChange, reset, submitAll]
    );

    return <PendingPolicyChangesContext.Provider value={value}>{children}</PendingPolicyChangesContext.Provider>;
}

function getErrorMessage(error: unknown): string {
    if (error instanceof Error && error.message) {
        return error.message;
    }
    return "Unknown error";
}

export function usePendingPolicyChanges() {
    const context = useContext(PendingPolicyChangesContext);
    if (!context) {
        throw new Error("usePendingPolicyChanges must be used within PendingPolicyChangesProvider");
    }
    return context;
}

export function PendingPolicyChangesBadge({ kind }: { kind: PendingPolicyChangeKind }) {
    const accountId = useAccountId();
    const { changes } = usePendingPolicyChanges();
    const value = changes.filter((change) => change.accountId === accountId && change.kind === kind).length;

    if (!value) {
        return null;
    }

    return (
        <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-semibold text-yellow-800">
            <ClockIcon className="h-3 w-3" />
            {value}
        </span>
    );
}

export function PendingPolicyChangesFooter({ placement = "fixed" }: { placement?: "fixed" | "inline" } = {}) {
    const accountId = useAccountId();
    const { changes, removeChange, reset, getResults, submitAll } = usePendingPolicyChanges();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isConfirmingReset, setIsConfirmingReset] = useState(false);
    const [submitResults, setSubmitResults] = useState<SubmitResult[] | null>(null);
    const [fixingChange, setFixingChange] = useState<PendingPolicyChange | null>(null);
    const accountChanges = changes.filter((change) => change.accountId === accountId);
    const count = accountChanges.length;

    if (!accountId || (!count && !submitResults)) {
        return null;
    }

    const undoResultChange = (id: string) => {
        removeChange(id);
        setSubmitResults((existing) => {
            const next = existing?.filter((existingResult) => existingResult.change.id !== id) || null;
            return next?.length ? next : null;
        });
    };
    const closeFixDialog = () => {
        setFixingChange(null);
        setSubmitResults(getResults(accountId));
    };

    return (
        <>
            {count ? (
                <div
                    className={
                        placement === "fixed"
                            ? "fixed inset-x-0 bottom-0 z-40 border-t border-stone-200 bg-white/95 px-4 py-3 shadow-[0_-4px_16px_rgba(0,0,0,0.08)] backdrop-blur lg:left-56"
                            : "border-t border-stone-200 bg-white/95 px-4 py-3"
                    }
                >
                    <div
                        className={`mx-auto flex flex-wrap items-center justify-between gap-4 ${
                            placement === "fixed" ? "max-w-6xl" : "w-full"
                        }`}
                    >
                        <div className="flex items-center gap-3">
                            <p className="text-sm font-medium text-stone-700">
                                {count} pending policy {count === 1 ? "change" : "changes"}
                            </p>
                            <button
                                type="button"
                                className="rounded border border-stone-300 bg-white px-3 py-1 text-sm font-medium text-stone-700 shadow-sm hover:bg-stone-50"
                                onClick={() => setSubmitResults(getResults(accountId))}
                            >
                                View
                            </button>
                        </div>
                        <div className="flex flex-wrap items-center justify-end gap-3">
                            <button
                                type="button"
                                className="inline-flex items-center gap-1 rounded px-2 py-1 text-sm text-stone-500 hover:bg-stone-100 hover:text-red-700"
                                onClick={() => setIsConfirmingReset(true)}
                            >
                                <XMarkIcon className="h-4 w-4" />
                                Reset
                            </button>
                            <PrimaryButton
                                disabled={isSubmitting}
                                onClick={async () => {
                                    setIsSubmitting(true);
                                    try {
                                        const results = await submitAll(accountId);
                                        const failures = results.filter((result) => result.status === "failed");
                                        setSubmitResults(results);
                                        if (failures.length) {
                                            useSnackbar.add(
                                                `${failures.length} policy change ${failures.length === 1 ? "request" : "requests"} failed`,
                                                {
                                                    variant: "error",
                                                }
                                            );
                                        } else {
                                            useSnackbar.add(`Policy change ${count === 1 ? "request" : "requests"} sent`, {
                                                variant: "success",
                                            });
                                        }
                                    } catch (e) {
                                        console.error(e);
                                        useSnackbar.add("Could not request all policy changes. Please try again.", {
                                            variant: "error",
                                        });
                                    } finally {
                                        setIsSubmitting(false);
                                    }
                                }}
                                className="min-w-48"
                            >
                                {isSubmitting ? (
                                    <CircularProgress size="1rem" classes={{ circle: "text-white" }} />
                                ) : (
                                    <ArrowUpTrayIcon className="h-4 w-4" />
                                )}
                                COMPLETE {count} {count === 1 ? "CHANGE" : "CHANGES"}
                            </PrimaryButton>
                        </div>
                    </div>
                </div>
            ) : null}
            <SimpleConfirmation
                open={isConfirmingReset}
                onClose={() => setIsConfirmingReset(false)}
                title="Discard Pending Policy Changes?"
                description={`This will remove all ${count} pending policy ${count === 1 ? "change" : "changes"}.`}
                confirmLabel="Discard changes"
                onConfirm={async () => {
                    reset(accountId);
                    setIsConfirmingReset(false);
                }}
            />
            <Dialog
                open={!!submitResults}
                onClose={() => setSubmitResults(null)}
                className="w-screen p-6 shadow-2xl md:max-w-3xl"
                baseZIndex={200}
            >
                <div className="w-full space-y-5">
                    <div className="flex items-start justify-between gap-4">
                        <div>
                            <p className="text-lg font-semibold text-stone-800">
                                {submitResults?.some((result) => result.status !== "pending")
                                    ? "Policy Change Request Results"
                                    : "Pending Policy Changes"}
                            </p>
                            <p className="text-sm text-stone-500">
                                {submitResults?.some((result) => result.status === "failed")
                                    ? "Fix or undo failed changes before requesting changes."
                                    : submitResults?.some((result) => result.status === "success")
                                    ? "Review what was requested successfully."
                                    : "These changes are pending and have not been requested yet. Click the 'Complete Changes' to send the change request to Navigator."}
                            </p>
                        </div>
                        <button
                            type="button"
                            className="rounded p-1 text-stone-500 hover:bg-stone-100"
                            onClick={() => setSubmitResults(null)}
                        >
                            <XMarkIcon className="h-5 w-5" />
                        </button>
                    </div>
                    <div className="max-h-[60vh] space-y-2 overflow-y-auto">
                        {submitResults?.map((result) => (
                            <div
                                key={result.change.id}
                                className={
                                    result.status === "failed"
                                        ? "rounded border border-red-200 bg-red-50 p-3"
                                        : result.status === "success"
                                        ? "rounded border border-emerald-200 bg-emerald-50 p-3"
                                        : "rounded border border-stone-200 bg-stone-50 p-3"
                                }
                            >
                                <div className="flex items-start justify-between gap-4">
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                            {result.status === "failed" ? (
                                                <ExclamationCircleIcon className="h-5 w-5 shrink-0 text-red-700" />
                                            ) : result.status === "success" ? (
                                                <CheckCircleIcon className="h-5 w-5 shrink-0 text-emerald-700" />
                                            ) : (
                                                <ClockIcon className="h-5 w-5 shrink-0 text-stone-500" />
                                            )}
                                            <p className="font-medium text-stone-800">
                                                {result.change.label} {result.change.detail ? ` ${result.change.detail}` : ""}
                                            </p>
                                            {result.change.supportingDocuments?.length ? (
                                                <span className="flex shrink-0 items-center gap-1 text-sm text-stone-500">
                                                    <PaperClipIcon className="h-4 w-4" />
                                                    {result.change.supportingDocuments.length}{" "}
                                                    {result.change.supportingDocuments.length === 1 ? "document" : "documents"}
                                                </span>
                                            ) : null}
                                        </div>
                                        {result.status === "failed" ? (
                                            <p className="mt-2 whitespace-pre-wrap rounded bg-white/70 p-2 text-sm text-red-800">
                                                {result.error}
                                            </p>
                                        ) : result.status === "success" ? (
                                            <p className="mt-1 text-sm text-emerald-700">Requested successfully</p>
                                        ) : (
                                            <p className="mt-1 text-sm text-stone-600">Pending</p>
                                        )}
                                    </div>
                                    {result.status !== "success" ? (
                                        <div className="flex shrink-0 self-center items-center gap-2">
                                            {result.status === "failed" && isFixableChange(result.change) ? (
                                                <button
                                                    type="button"
                                                    className="rounded border border-stone-300 bg-white px-2 py-1 text-sm font-medium text-stone-700 hover:bg-stone-100"
                                                    onClick={() => setFixingChange(result.change)}
                                                >
                                                    Fix
                                                </button>
                                            ) : null}
                                            <button
                                                type="button"
                                                className="inline-flex items-center gap-1 rounded border border-red-200 bg-white px-2 py-1 text-sm font-medium text-red-700 hover:bg-red-100"
                                                onClick={() => undoResultChange(result.change.id)}
                                            >
                                                <ArrowUturnLeftIcon className="h-4 w-4" />
                                                Undo
                                            </button>
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </Dialog>
            <Suspense fallback={null}>
                {fixingChange?.unitUpdate?.kind === "drivers" ? (
                    <NewDriverDialog
                        open
                        onClose={closeFixDialog}
                        defaultValues={fixingChange.unitUpdate.originalValues}
                        editingId={fixingChange.unitUpdate.id}
                        dialogProps={{ baseZIndex: 300 }}
                        deferPolicyChanges
                        pendingChangeDetail={fixingChange.detail}
                    />
                ) : fixingChange?.unitUpdate?.kind === "equipment" ? (
                    <NewUnitDialog
                        open
                        onClose={closeFixDialog}
                        unitType={fixingChange.unitUpdate.type}
                        defaultValues={fixingChange.unitUpdate.originalValues}
                        editingId={fixingChange.unitUpdate.id}
                        dialogProps={{ baseZIndex: 300 }}
                        deferPolicyChanges
                        pendingChangeDetail={fixingChange.detail}
                    />
                ) : null}
            </Suspense>
        </>
    );
}
